import React, { useMemo } from 'react';
import { FiFolder, FiFile, FiShare2, FiHardDrive } from 'react-icons/fi';
import { formatBytes } from '../api/storageBoxApi';
import { STORAGE_BOX_ROOT_LABEL } from '../constants';
import { useStorageBox } from '../StorageBoxContext';
import { StorageBoxPreviewSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';

function formatWhen(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function folderInsights(items) {
  let folders = 0;
  let files = 0;
  let bytes = 0;
  let shared = 0;
  let latest = null;
  for (const item of items) {
    if (item.type === 'folder') folders += 1;
    else {
      files += 1;
      bytes += item.sizeBytes || 0;
    }
    if (item.isShared) shared += 1;
    const ts = item.updatedAt || item.createdAt;
    if (ts && (!latest || new Date(ts) > new Date(latest.ts))) {
      latest = { name: item.name, type: item.type, ts };
    }
  }
  return { folders, files, bytes, shared, latest, total: items.length };
}

export default function StorageBoxPreviewOverview({ className = '' }) {
  const {
    readOnly,
    quota,
    workspaceStats,
    items,
    breadcrumbs,
    loading,
    searchActive,
    publicToken,
  } = useStorageBox();

  const folderPath = useMemo(() => {
    if (!breadcrumbs.length) return STORAGE_BOX_ROOT_LABEL;
    return breadcrumbs.map((b) => b.name).join(' / ');
  }, [breadcrumbs]);

  const folder = useMemo(() => folderInsights(items), [items]);

  const usagePct =
    quota && !quota.unlimited && quota.limitBytes
      ? Math.min(100, (quota.usedBytes / quota.limitBytes) * 100)
      : 0;

  const freeBytes =
    quota && !quota.unlimited && quota.limitBytes != null
      ? Math.max(0, quota.limitBytes - (quota.usedBytes || 0))
      : null;

  const asideClass = ['sb-preview', 'sb-preview--details', 'sb-preview--overview', className]
    .filter(Boolean)
    .join(' ');

  if (searchActive) {
    return (
      <aside className={asideClass}>
        <h3 className="sb-preview-overview__title">Search</h3>
        <p className="sb-preview-overview__lead">
          Select a result to see full details, preview, and sharing options.
        </p>
      </aside>
    );
  }

  if (readOnly || publicToken) {
    return (
      <aside className={asideClass}>
        <header className="sb-preview-overview__head">
          <FiHardDrive className="sb-preview-overview__icon" aria-hidden />
          <h3 className="sb-preview-overview__title">Shared folder</h3>
        </header>
        <p className="sb-preview-overview__location" title={folderPath}>{folderPath}</p>
        {loading ? (
          <StorageBoxPreviewSkeleton compact />
        ) : (
          <ul className="sb-preview-overview__stats">
            <li>
              <FiFolder aria-hidden />
              <span>{folder.folders} folder{folder.folders === 1 ? '' : 's'}</span>
            </li>
            <li>
              <FiFile aria-hidden />
              <span>{folder.files} file{folder.files === 1 ? '' : 's'}</span>
            </li>
            {folder.files > 0 ? (
              <li>
                <FiHardDrive aria-hidden />
                <span>{formatBytes(folder.bytes)} in this folder</span>
              </li>
            ) : null}
          </ul>
        )}
        <p className="sb-preview-overview__tip">Tap a file or folder for properties and actions.</p>
      </aside>
    );
  }

  if (loading && !quota) {
    return (
      <aside className={asideClass}>
        <StorageBoxPreviewSkeleton />
      </aside>
    );
  }

  const ws = workspaceStats || {};
  const avgFileSize = ws.fileCount > 0 && ws.totalFileBytes ? ws.totalFileBytes / ws.fileCount : null;

  return (
    <aside className={asideClass}>
      <header className="sb-preview-overview__head">
        <FiHardDrive className="sb-preview-overview__icon" aria-hidden />
        <h3 className="sb-preview-overview__title">Storage overview</h3>
      </header>

      {quota ? (
        <section className="sb-preview-overview__section" aria-label="Storage space">
          <h4 className="sb-preview-overview__section-title">Space</h4>
          {quota.unlimited ? (
            <p className="sb-preview-overview__metric">Unlimited storage on your plan</p>
          ) : (
            <>
              <p className="sb-preview-overview__metric">
                <strong>{formatBytes(quota.usedBytes)}</strong> used
                {quota.limitBytes ? ` · ${formatBytes(quota.limitBytes)} total` : ''}
              </p>
              <div className="sb-quota-bar sb-preview-overview__quota-bar" aria-hidden>
                <span style={{ width: `${usagePct}%` }} />
              </div>
              {freeBytes != null ? (
                <p className="sb-preview-overview__sub">
                  {formatBytes(freeBytes)} free ({Math.round(100 - usagePct)}% available)
                </p>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <section className="sb-preview-overview__section" aria-label="Workspace totals">
        <h4 className="sb-preview-overview__section-title">Your library</h4>
        <ul className="sb-preview-overview__stats">
          <li>
            <FiFile aria-hidden />
            <span>
              <strong>{ws.fileCount ?? '—'}</strong> files
              {ws.totalFileBytes != null ? ` · ${formatBytes(ws.totalFileBytes)}` : ''}
            </span>
          </li>
          <li>
            <FiFolder aria-hidden />
            <span><strong>{ws.folderCount ?? '—'}</strong> folders</span>
          </li>
          {ws.sharedItemCount > 0 ? (
            <li>
              <FiShare2 aria-hidden />
              <span><strong>{ws.sharedItemCount}</strong> shared item{ws.sharedItemCount === 1 ? '' : 's'}</span>
            </li>
          ) : null}
        </ul>
        {avgFileSize != null && avgFileSize > 0 ? (
          <p className="sb-preview-overview__sub">Average file size · {formatBytes(avgFileSize)}</p>
        ) : null}
      </section>

      <section className="sb-preview-overview__section" aria-label="Current folder">
        <h4 className="sb-preview-overview__section-title">This folder</h4>
        <p className="sb-preview-overview__location" title={folderPath}>{folderPath}</p>
        {loading ? (
          <StorageBoxPreviewSkeleton compact />
        ) : (
          <ul className="sb-preview-overview__stats sb-preview-overview__stats--compact">
            <li>
              {folder.total === 0 ? (
                <span>Empty — drop files here or use New</span>
              ) : (
                <span>
                  <strong>{folder.total}</strong> item{folder.total === 1 ? '' : 's'} (
                  {folder.folders} folder{folder.folders === 1 ? '' : 's'}, {folder.files} file
                  {folder.files === 1 ? '' : 's'})
                </span>
              )}
            </li>
            {folder.files > 0 ? (
              <li>
                <span>{formatBytes(folder.bytes)} of files here</span>
              </li>
            ) : null}
            {folder.shared > 0 ? (
              <li>
                <FiShare2 aria-hidden />
                <span>{folder.shared} shared in this folder</span>
              </li>
            ) : null}
            {folder.latest ? (
              <li className="sb-preview-overview__recent">
                <span>
                  Latest change · <strong>{folder.latest.name}</strong>
                  {formatWhen(folder.latest.ts) ? ` (${formatWhen(folder.latest.ts)})` : ''}
                </span>
              </li>
            ) : null}
          </ul>
        )}
      </section>

      <p className="sb-preview-overview__tip">
        Open a folder or select a file to see previews, sharing, and full properties.
      </p>
    </aside>
  );
}
